/**
 * QuickBytesCarouselModal - story-style reader for the highlights deck.
 *
 * Design intent: the card *is* the modal. Chrome (progress bars, header
 * meta, close button) floats in the margins so it doesn't compete with
 * the card content. Continuous navigation across concepts means the
 * user can keep reading until they explicitly close.
 *
 * Layout (top → bottom):
 *
 *   Progress bars   - pinned to the absolute top edge, 3px tall
 *   Concept name    - typography, no surrounding chrome
 *   Meta strip      - source lesson · due-today · domain tags (small)
 *   Card body       - title + body, generous typography, full height
 *   Floating ← →    - appear on hover at left/right edges of the body
 *   Card counter    - centered, muted, at the bottom
 *
 * The close X floats in the top-right corner of the modal, outside the
 * header text flow. Tap halves (left/right 45%) and keyboard (←/→) and
 * the floating chevrons all route through `goPrev` / `goNext`.
 *
 *   Within concept   → cardIdx ± 1
 *   At concept edge  → conceptIdx ± 1, cardIdx jumps to 0 or last
 *   At deck edge     → wrap to opposite end of deck
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Bookmark, Loader2, X } from 'lucide-react';
import { Modal } from '@ai-tutor/ui';
import { useToggleBookmark } from '@/hooks/mindspace/useRevision';
import { cn } from '@/lib/utils';
import type { HighlightsFeedCarousel } from '@/lib/api-client';
import {
  TemplateCard,
  pickTemplateForConcept,
} from '@/components/features/highlights-template';
import {
  advanceCursor,
  retreatCursor,
  shouldPrefetch,
} from '@/lib/mindspace/carouselCursor';
import {
  CarouselChevrons,
  CarouselDots,
  CarouselHeaderMeta,
  CarouselTapZones,
  ScaledCardCanvas,
} from '@/components/features/mindspace/quick-bytes/CarouselChrome';
import { PhoneCarouselReader } from '@/components/features/mindspace/quick-bytes/PhoneCarouselReader';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

const SWIPE_THRESHOLD = 50;

interface QuickBytesCarouselModalProps {
  carousels: HighlightsFeedCarousel[];
  /** concept_id of the carousel to open with. Null = modal closed. */
  initialConceptId: string | null;
  onClose: () => void;
  /** Infinite-query plumbing passed through from `QuickBytesGrid` so
   *  modal navigation can extend the deck without forcing the user to
   *  close and scroll. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

export function QuickBytesCarouselModal({
  carousels,
  initialConceptId,
  onClose,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: QuickBytesCarouselModalProps) {
  const isOpen = initialConceptId !== null;
  const toggleBookmark = useToggleBookmark();

  const [conceptIdx, setConceptIdx] = useState(0);
  const [cardIdx, setCardIdx] = useState(0);
  /** Set when the user hits Next at the loaded edge while more pages
   *  exist - we trigger a fetch and resolve the advance once the new
   *  page lands (see effect below). */
  const [pendingAdvance, setPendingAdvance] = useState(false);
  const advanceTargetRef = useRef<number | null>(null);

  // Seed the cursor on close→open transitions only. Once the modal is
  // open, nav is fully internal - re-renders from the parent shouldn't
  // reset the user's position.
  const lastInitialIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialConceptId === null) {
      lastInitialIdRef.current = null;
      return;
    }
    if (lastInitialIdRef.current === initialConceptId) return;
    lastInitialIdRef.current = initialConceptId;
    const idx = carousels.findIndex(c => c.concept_id === initialConceptId);
    setConceptIdx(Math.max(0, idx));
    setCardIdx(0);
  }, [initialConceptId, carousels]);

  const totalConcepts = carousels.length;
  const currentCarousel = carousels[conceptIdx];
  const currentCard = currentCarousel?.cards[cardIdx];

  // Both directions delegate to `lib/mindspace/carouselCursor`, which is
  // table-tested over every position and both wrap points. What stays here is
  // the part that is not arithmetic: turning a `fetch` outcome into a deferred
  // advance with a loading overlay (§2.12 #14).
  const goNext = useCallback(() => {
    const result = advanceCursor(
      carousels,
      { conceptIdx, cardIdx },
      !!hasNextPage && !!fetchNextPage
    );
    if (result.kind === 'none') return;
    if (result.kind === 'fetch') {
      advanceTargetRef.current = result.target;
      setPendingAdvance(true);
      if (!isFetchingNextPage) fetchNextPage?.();
      return;
    }
    setConceptIdx(result.cursor.conceptIdx);
    setCardIdx(result.cursor.cardIdx);
  }, [
    carousels,
    conceptIdx,
    cardIdx,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const goPrev = useCallback(() => {
    const result = retreatCursor(carousels, { conceptIdx, cardIdx });
    if (result.kind !== 'move') return;
    setConceptIdx(result.cursor.conceptIdx);
    setCardIdx(result.cursor.cardIdx);
  }, [carousels, conceptIdx, cardIdx]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, goNext, goPrev]);

  // Silent prefetch: when the user is within PREFETCH_THRESHOLD of the
  // loaded edge, kick off the next page so it's usually ready by the
  // time they cross. React Query dedupes if a fetch is already in
  // flight, so we don't have to guard.
  useEffect(() => {
    if (!isOpen) return;
    if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return;
    if (shouldPrefetch(conceptIdx, totalConcepts)) {
      fetchNextPage();
    }
  }, [
    isOpen,
    conceptIdx,
    totalConcepts,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // Resolve a deferred advance once new data lands. If the target
  // index is now reachable, jump to it. If we've finished loading and
  // there are no more pages, fall back to the wrap-to-start behavior.
  useEffect(() => {
    if (!pendingAdvance) return;
    const target = advanceTargetRef.current;
    if (target == null) return;
    if (target < totalConcepts) {
      setConceptIdx(target);
      setCardIdx(0);
      advanceTargetRef.current = null;
      setPendingAdvance(false);
      return;
    }
    if (!isFetchingNextPage && !hasNextPage) {
      setConceptIdx(0);
      setCardIdx(0);
      advanceTargetRef.current = null;
      setPendingAdvance(false);
    }
  }, [pendingAdvance, totalConcepts, isFetchingNextPage, hasNextPage]);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    touchStartRef.current = null;

    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;

    if (dx < 0) goNext();
    else goPrev();
  };

  // Size the card as a true square = the largest that fits the body area, then
  // scale a fixed CARD_DESIGN_PX canvas into it (like an image) so layout stays
  // proportional at any modal size.
  const cardAreaRef = useRef<HTMLDivElement>(null);
  const [cardSide, setCardSide] = useState(0);
  useEffect(() => {
    const el = cardAreaRef.current;
    if (!el) return;
    const update = () =>
      setCardSide(Math.floor(Math.min(el.clientWidth, el.clientHeight)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  const phone = useMediaQuery('(max-width: 767.98px)');

  if (!isOpen || !currentCarousel || !currentCard) return null;

  const toggleCurrentBookmark = () =>
    toggleBookmark.mutate({
      conceptId: currentCarousel.concept_id,
      nextBookmarked: !currentCarousel.is_bookmarked,
    });

  const card = (
    <ScaledCardCanvas side={cardSide} resetKey={`${conceptIdx}-${cardIdx}`}>
      <TemplateCard
        template={pickTemplateForConcept(currentCarousel.concept_id)}
        card={currentCard}
        slideIndex={cardIdx}
        totalSlides={currentCarousel.cards.length}
        conceptName={currentCarousel.concept_name}
      />
    </ScaledCardCanvas>
  );

  // Pending-advance loading overlay - shown only when the user navigates
  // off the loaded edge and the next page hasn't landed yet. Sits above the
  // tap zones (z-30) so clicks don't fight with goNext while we're waiting.
  const loadingMore = pendingAdvance && (
    <div className='absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm'>
      <Loader2 className='h-6 w-6 animate-spin text-brand-600 dark:text-primary' />
      <p className='text-sm text-muted-foreground'>Loading more highlights…</p>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='xl'
      showCloseButton={false}
      // The deck is `role="dialog" aria-modal="true"` with no visible title —
      // `showCloseButton={false}` suppresses the primitive's whole header so
      // the card can be the modal. Without this the dialog had NO accessible
      // name at all (axe `aria-dialog-name`), so opening a deck announced
      // "dialog" and nothing else. The concept name is what the reader needs,
      // and it changes as they navigate across concepts.
      ariaLabel={`${currentCarousel.concept_name} highlights`}
      // Full screen below `md`: a story-style reader is the whole screen on a
      // phone. As a floating box it lost 16px a side and a band top and
      // bottom, and the card sat in empty space in the middle.
      className='max-md:m-0 max-md:h-[100dvh] max-md:max-h-none max-md:max-w-none'
      contentClassName='h-[90vh] group/modal max-md:h-full max-md:rounded-none max-md:border-0'
    >
      {phone ? (
        <PhoneCarouselReader
          carousel={currentCarousel}
          cardIdx={cardIdx}
          upNext={upNextName(carousels, conceptIdx, !!hasNextPage)}
          onPrev={goPrev}
          onNext={goNext}
          onClose={onClose}
          onToggleBookmark={toggleCurrentBookmark}
          cardAreaRef={cardAreaRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {card}
          {loadingMore}
          <CarouselTapZones onPrev={goPrev} onNext={goNext} />
        </PhoneCarouselReader>
      ) : (
        <div className='relative flex h-full flex-col'>
          {/* Floating action buttons - bookmark + close. Top-right
            corner, outside the header text flow. Bookmark sits to the
            left of close so the most destructive (closing) action is
            farthest from where the eye lands. */}
          <button
            type='button'
            onClick={toggleCurrentBookmark}
            aria-label={
              currentCarousel.is_bookmarked ? 'Remove bookmark' : 'Add bookmark'
            }
            aria-pressed={currentCarousel.is_bookmarked}
            className={cn(
              'absolute right-16 top-6 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full shadow-sm backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:focus-visible:ring-primary/40 max-md:h-11 max-md:w-11',
              currentCarousel.is_bookmarked
                ? 'dark:tint-brand-strong bg-brand-50/70 text-brand-500 hover:bg-brand-100 dark:hover:bg-primary/20'
                : 'bg-background/80 text-foreground/70 hover:bg-background hover:text-foreground'
            )}
          >
            <Bookmark
              className={cn(
                'h-4 w-4',
                currentCarousel.is_bookmarked && 'fill-current'
              )}
            />
          </button>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='absolute right-4 top-6 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/80 text-foreground/70 shadow-sm backdrop-blur transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:focus-visible:ring-primary/40 max-md:h-11 max-md:w-11'
          >
            <X className='h-4 w-4' />
          </button>

          {/* Header - typography only, no border, no chrome. Extra right
            padding leaves room for the floating bookmark + close
            buttons in the top-right corner. */}
          <CarouselHeaderMeta carousel={currentCarousel} />

          {/* Card body - the hero. Tap zones overlaid; floating chevrons
            appear on modal hover so the navigation surface is
            discoverable on desktop without permanent chrome. */}
          <div
            className='relative min-h-0 flex-1 overflow-hidden'
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={cardAreaRef}
              className='absolute inset-4 flex items-center justify-center'
            >
              {card}
            </div>

            {loadingMore}

            <CarouselTapZones onPrev={goPrev} onNext={goNext} />

            <CarouselChevrons onPrev={goPrev} onNext={goNext} />
          </div>

          {/* Pagination dots - card position within the current concept,
            centered at the bottom (replaces the top progress bars). */}
          <CarouselDots
            count={currentCarousel.cards.length}
            activeIndex={cardIdx}
          />
        </div>
      )}
    </Modal>
  );
}

/**
 * Where Next goes from a concept's last card, for the phone's "Up next" line:
 * the next concept, or - at the end of what is loaded - nothing while more is
 * coming, and the first concept again once the deck wraps.
 */
function upNextName(
  carousels: HighlightsFeedCarousel[],
  conceptIdx: number,
  morePages: boolean
): string | null {
  const next = carousels[conceptIdx + 1];
  if (next) return next.concept_name;
  if (morePages || carousels.length < 2) return null;
  return carousels[0].concept_name;
}
